import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listProjects, projectExportUrl, projectPreviewUrl, type Project } from "../api";
import { CommunityCard } from "../components/CommunityCard";
import { Icon } from "../components/Icon";
import { SG_FLAG, SOCLOSE } from "../constants/soclose";
import { LANGS, useLang, useT } from "../i18n";

type View = "explore" | "tendances" | "recents" | "categories" | "tarifs";

// 3DPrintForge — Vitrine / Explore: public creation dashboard (design: forge/3DPrintForge
// Vitrine.html). Free browsing; download / remix / generate / upload open the sign-up gate,
// then funnel into the real app. Recreated from the design bundle, connected to live projects.

const MATC: Record<string, string> = {
  PLA: "#d94f4f", PETG: "#2f9e8f", ABS: "#5b6273", RESIN: "#9fb7d4", NYLON: "#d9b44f",
  TPU: "#6c5ce7", PC: "#7d8694", WOOD: "#9c6b3f", METAL: "#b8bcc4",
};
function matColor(m: string): string {
  const v = (m || "").toUpperCase();
  for (const k in MATC) if (v.includes(k)) return MATC[k];
  return MATC.PLA;
}
function scoreColor(s: number): string {
  return s >= 0.85 ? "var(--ok)" : s >= 0.7 ? "var(--warn)" : "var(--bad)";
}
function eur(p: number): string {
  return p.toFixed(2).replace(".", ",") + " €";
}

// Isometric procedural part thumbnail, colored by material.
function Iso({ color, size = 200 }: { color: string; size?: number }) {
  const s = 52, w = s, h = s * 0.5, bx = 2.7, by = 2.1, bz = 2.0;
  const P = (px: number, py: number, pz: number) =>
    `${((px - py) * w).toFixed(1)},${((px + py) * h - pz * s).toFixed(1)}`;
  const top = [P(0, 0, bz), P(bx, 0, bz), P(bx, by, bz), P(0, by, bz)].join(" ");
  const right = [P(bx, 0, bz), P(bx, by, bz), P(bx, by, 0), P(bx, 0, 0)].join(" ");
  const front = [P(0, by, bz), P(bx, by, bz), P(bx, by, 0), P(0, by, 0)].join(" ");
  const hx = (bx * 0.62 - by * 0.5) * w, hy = (bx * 0.62 + by * 0.5) * h - bz * s;
  return (
    <svg width={size} height={size} viewBox="-150 -165 300 300" style={{ overflow: "visible", filter: "drop-shadow(0 20px 26px rgba(0,0,0,0.5))" }}>
      <ellipse cx={0} cy={(bx + by) * h * 0.5} rx={(bx + by) * w * 0.5} ry={(bx + by) * h * 0.5} fill="#000" opacity="0.28" />
      <polygon points={front} fill={`color-mix(in oklab, ${color} 60%, black)`} stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
      <polygon points={right} fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth="0.6" />
      <polygon points={top} fill={`color-mix(in oklab, ${color} 86%, white)`} stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
      <ellipse cx={hx} cy={hy} rx={w * 0.42} ry={h * 0.42} fill="#0a0e16" opacity="0.85" />
    </svg>
  );
}

interface GModel {
  id: string; title: string; author: string; ac: string; material: string;
  type: "parametric" | "generative"; use: string; cat: string; score: number;
  downloads: number; likes: number; h: number; price: number;
  real?: boolean; projectId?: string; img?: string; ai?: boolean;
}

const USES: [string, string][] = [["all", "Tous"], ["maison", "Maison"], ["atelier", "Atelier"], ["deco", "Déco"], ["rc", "RC & Drone"], ["meca", "Mécanique"], ["optique", "Optique"]];
const A = ["Léa M.", "Tom R.", "Sora K.", "Max V.", "Inès D.", "Yann P.", "Nora B.", "Hugo L.", "Mia T.", "Ravi S."];
const ACOL = ["#34d399", "#60a5fa", "#fbbf24", "#a78bfa", "#56d4e3", "#f87171"];

let _id = 0;
function mk(title: string, mat: string, type: "parametric" | "generative", use: string, cat: string, score: number, dl: number, likes: number, h: number): GModel {
  const a = A[_id % A.length];
  const i = _id++;
  return { id: "m" + i, title, author: a, ac: ACOL[(title.length + i) % ACOL.length], material: mat, type, use, cat, score, downloads: dl, likes, h, price: type === "generative" ? [3.9, 4.9, 5.9, 2.9][i % 4] : (i % 4 === 0 ? 1.9 : 0) };
}
const MODELS: GModel[] = [
  mk("Support caméra inclinable 60°", "PETG", "parametric", "optique", "vedette", 0.96, 2140, 312, 250),
  mk("Boîtier capteur IP54", "ASA", "parametric", "maison", "vedette", 0.94, 1890, 256, 200),
  mk("Dragon enroulé · base ronde", "RESIN", "generative", "deco", "tendance", 0.88, 4320, 901, 270),
  mk("Réducteur planétaire M1.5", "PLA", "parametric", "meca", "vedette", 0.97, 980, 188, 210),
  mk("Bras de drone 5\" nervuré", "NYLON", "parametric", "rc", "tendance", 0.95, 1560, 274, 190),
  mk("Cache-pot facetté · drainage", "PLA", "generative", "deco", "tendance", 0.86, 3110, 612, 260),
  mk("Crochet mural charge 5 kg", "PETG", "parametric", "atelier", "recent", 0.95, 760, 142, 180),
  mk("Organisateur tiroir modulaire", "PLA", "parametric", "maison", "tendance", 0.93, 2670, 405, 230),
  mk("Berceau caméra anti-vibration", "TPU", "parametric", "rc", "recent", 0.9, 540, 98, 200),
  mk("Engrenage droit 24 dents", "PLA", "parametric", "meca", "recent", 0.96, 1230, 176, 175),
  mk("Bouchon d'objectif 52 mm", "PETG", "parametric", "optique", "recent", 0.94, 410, 64, 165),
  mk("Buste low-poly · socle hex", "RESIN", "generative", "deco", "tendance", 0.84, 2980, 720, 280),
  mk("Plaque centrale FPV", "PC", "parametric", "rc", "vedette", 0.92, 880, 134, 195),
  mk("Pale de turbine vrillée", "ABS", "parametric", "meca", "recent", 0.89, 320, 51, 210),
  mk("Support casque mural", "PLA", "parametric", "maison", "tendance", 0.93, 1740, 288, 185),
  mk("Vase spiralé translucide", "RESIN", "generative", "deco", "recent", 0.86, 1020, 233, 255),
  mk("Chape d'articulation à axe", "PETG", "parametric", "meca", "recent", 0.91, 290, 47, 170),
  mk("Embout de canne TPU", "TPU", "parametric", "maison", "recent", 0.9, 620, 88, 175),
];

const PUB_TARGETS = [
  { id: "store_soclose", name: "store.soclose.co", mode: "auto", sell: true, note: "Notre boutique — vente directe, upload API." },
  { id: "myminifactory", name: "MyMiniFactory", mode: "auto", sell: true, note: "Upload API v2 (OAuth) — gratuit ou payant." },
  { id: "thingiverse", name: "Thingiverse", mode: "auto", sell: false, note: "Upload API — partage gratuit uniquement." },
  { id: "cults3d", name: "Cults3D", mode: "prepare", sell: true, note: "Vente — fiche préparée, upload manuel." },
  { id: "gumroad", name: "Gumroad", mode: "prepare", sell: true, note: "Vente directe — fiche + ZIP préparés." },
  { id: "printables", name: "Printables", mode: "prepare", sell: false, note: "Gratuit — fiche préparée, upload manuel." },
];

// Map a free-form project group onto a fixed use-filter slug so real projects actually
// match the "Cas d'usage" filters (otherwise they only ever showed under "Tous"/"Atelier").
const GROUP_TO_USE: [string, string][] = [
  ["drone", "rc"], ["rc", "rc"], ["fpv", "rc"], ["avion", "rc"], ["aile", "rc"],
  ["meca", "meca"], ["méca", "meca"], ["engren", "meca"], ["turbine", "meca"],
  ["maison", "maison"], ["home", "maison"], ["deco", "deco"], ["déco", "deco"],
  ["optiq", "optique"], ["camera", "optique"], ["caméra", "optique"], ["lens", "optique"],
  ["atelier", "atelier"], ["outil", "atelier"],
];
function groupToUse(g: string | null): string {
  const k = (g || "").toLowerCase();
  for (const [needle, slug] of GROUP_TO_USE) if (k.includes(needle)) return slug;
  return "atelier";
}
// Map a real project into a gallery card (real preview image, links to its detail page).
function projectToModel(p: Project, i: number): GModel {
  const mat = (p.preview_material || "PLA").toUpperCase();
  return {
    id: "p_" + p.id, title: p.name, author: "3DPrintForge", ac: ACOL[i % ACOL.length], material: mat,
    type: "parametric", use: groupToUse(p.group), cat: "vedette",
    score: p.preview_passed === false ? 0.7 : 0.96, downloads: 120 + i * 37, likes: 24 + i * 9,
    h: [250, 200, 230][i % 3], price: 0, real: true, projectId: p.id, img: projectPreviewUrl(p.id),
  };
}

type UpState = { file: string; title: string; desc: string; tags: string; cat: string; use: string; monet: string; price: number; license: string; targets: string[] };

// "Compatible with your tools" marquee — printers, hosts/firmware, slicers, CAD and marketplaces
// our pipeline imports from, validates for, or routes to. Wordmarks (not trademarked logos) so it
// renders everywhere and stays deploy-safe; a category glyph gives each one a logo-like feel.
const BRANDS_A: [string, string][] = [
  ["Prusa", "printer"], ["Bambu Lab", "printer"], ["Creality", "printer"], ["Anycubic", "printer"],
  ["Elegoo", "printer"], ["UltiMaker", "printer"], ["Formlabs", "printer"], ["Raise3D", "printer"],
  ["Voron", "printer"], ["Sovol", "printer"], ["Klipper", "cpu"], ["OctoPrint", "cpu"],
  ["Moonraker", "cpu"], ["Marlin", "cpu"], ["PrusaLink", "cpu"], ["Duet", "cpu"],
];
const BRANDS_B: [string, string][] = [
  ["PrusaSlicer", "layers"], ["UltiMaker Cura", "layers"], ["OrcaSlicer", "layers"], ["Bambu Studio", "layers"],
  ["Simplify3D", "layers"], ["Fusion 360", "studio"], ["FreeCAD", "studio"], ["Blender", "studio"],
  ["Onshape", "studio"], ["STL", "box"], ["3MF", "box"], ["STEP", "box"],
  ["Printables", "share"], ["MakerWorld", "share"], ["Thingiverse", "share"], ["Cults3D", "share"],
];

function BrandRow({ items, rev }: { items: [string, string][]; rev?: boolean }) {
  const loop = [...items, ...items]; // duplicated once so a -50% translate loops seamlessly
  return (
    <div className="logo-marquee">
      <div className={`logo-track${rev ? " rev" : ""}`}>
        {loop.map(([name, ico], i) => (
          <span className="logo-pill" key={`${name}-${i}`}><Icon name={ico} size={16} /> {name}</span>
        ))}
      </div>
    </div>
  );
}

function BrandStrip({ tr }: { tr: (s: string) => string }) {
  return (
    <section className="logostrip">
      <div className="logostrip-head">
        <div className="eyebrow"><span className="dot" /> {tr("Compatible avec vos outils")}</div>
        <h2>{tr("Tout ton écosystème, déjà connecté")}</h2>
        <p>{tr("Importe, valide et route vers les imprimantes, slicers, firmwares, CAO et marketplaces que tu utilises déjà.")}</p>
      </div>
      <BrandRow items={BRANDS_A} />
      <BrandRow items={BRANDS_B} rev />
    </section>
  );
}

const VIEW_PATHS: Record<View, string> = { explore: "/", tendances: "/tendances", recents: "/recents", categories: "/categories", tarifs: "/tarifs" };
const VIEW_TITLES: Record<View, string> = { explore: "Crée des objets 3D imprimables, en une phrase.", tendances: "Les modèles qui montent", recents: "Les dernières créations", categories: "Parcourir par catégorie", tarifs: "Choisis ta formule" };

export function LandingPage({ initialView = "explore" }: { initialView?: View }) {
  const nav = useNavigate();
  const tr = useT();
  const { lang, setLang } = useLang();
  const [view, setView] = useState<View>(initialView);

  // Keep the URL + tab title in sync with the current view (shareable deep links,
  // no router remount: replaceState only).
  useEffect(() => {
    const path = VIEW_PATHS[view];
    if (window.location.pathname !== path) window.history.replaceState(null, "", path);
    document.title = view === "explore"
      ? "3DPrintForge · " + tr("Crée des objets 3D imprimables, en une phrase.")
      : tr(VIEW_TITLES[view]) + " · 3DPrintForge";
  });
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [auth, setAuth] = useState(false);
  const [user, setUser] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("register");
  const [reason, setReason] = useState("");
  const pending = useRef<(() => void) | null>(null);
  const [toast, setToast] = useState<{ m: string; k: string } | null>(null);

  const [mode, setMode] = useState<"text" | "image">("text");
  const [prompt, setPrompt] = useState("");
  const [cat, setCat] = useState("all");
  const [type, setType] = useState("all");
  const [use, setUse] = useState("all");
  const [price, setPrice] = useState("all");
  const [sort, setSort] = useState("reco");
  const [likes, setLikes] = useState<Record<string, number>>({});
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [realModels, setRealModels] = useState<GModel[]>([]);
  const [imgFail, setImgFail] = useState<Record<string, boolean>>({});
  const [heroSlides, setHeroSlides] = useState<{ id: string; img: string; title: string; sub: string }[]>([]);
  const [aiCards, setAiCards] = useState<GModel[]>([]);
  const [modelImgs, setModelImgs] = useState<Record<string, string>>({});
  const [catImgs, setCatImgs] = useState<Record<string, string>>({});
  const [pricingBanner, setPricingBanner] = useState("");
  const [communityBanner, setCommunityBanner] = useState("");
  const [slide, setSlide] = useState(0);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [up, setUp] = useState<UpState>({ file: "", title: "", desc: "", tags: "", cat: "vedette", use: "maison", monet: "free", price: 4.9, license: "CC-BY", targets: ["store_soclose"] });

  useEffect(() => {
    listProjects()
      .then((ps) => setRealModels(ps.filter((p) => (p.ok_count ?? 0) > 0 || p.preview_file_id).map(projectToModel)))
      .catch(() => {});
    // AI-generated showcase (kie.ai / nano-banana-pro): hero slider + featured gallery cards.
    fetch("/showcase/showcase.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { hero?: { id: string; img: string; title: string; sub: string }[]; cards?: { id: string; img: string; title: string; use: string; material: string }[]; models?: Record<string, string>; cats?: Record<string, string>; pricingBanner?: string; communityBanner?: string } | null) => {
        if (!d) return;
        setHeroSlides(d.hero ?? []);
        setModelImgs(d.models ?? {});
        setCatImgs(d.cats ?? {});
        setPricingBanner(d.pricingBanner ?? "");
        setCommunityBanner(d.communityBanner ?? "");
        // Idle-prefetch the view images so switching to Catégories/Tarifs/Communauté is instant.
        const warm = () => [...Object.values(d.cats ?? {}), d.pricingBanner, d.communityBanner]
          .filter((u): u is string => !!u).forEach((u) => { const im = new Image(); im.src = u; });
        if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 3000 }); else setTimeout(warm, 1200);
        setAiCards((d.cards ?? []).map((c, i): GModel => ({
          id: "ai_" + c.id, title: c.title, author: "Forge IA", ac: "#34d399", material: c.material,
          type: "generative", use: c.use, cat: i % 2 ? "tendance" : "vedette", score: 0.97,
          downloads: 800 + i * 120, likes: 180 + i * 30, h: 250, price: 0, real: true, ai: true, img: c.img,
        })));
      })
      .catch(() => {});
  }, []);

  // Auto-advance the hero slider.
  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  // Escape closes the open modal; lock background scroll while one is open.
  useEffect(() => {
    if (!authOpen && !uploadOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setAuthOpen(false); setUploadOpen(false); } };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [authOpen, uploadOpen]);

  function flash(m: string, k: string) { setToast({ m, k }); setTimeout(() => setToast(null), 2200); }
  function gate(why: string, fn: () => void) { if (auth) { fn(); return; } setReason(why); pending.current = fn; setAuthTab("register"); setAuthOpen(true); }
  function doAuth() {
    setAuth(true);
    setUser((document.getElementById("au-name") as HTMLInputElement | null)?.value || "Toi");
    setAuthOpen(false); flash("Bienvenue — compte connecté", "ok");
    if (pending.current) { const f = pending.current; pending.current = null; setTimeout(f, 120); }
  }

  function download(m: GModel) {
    gate(`Connecte-toi pour télécharger « ${m.title} » (STL + notice).`, () => {
      if (m.real && m.projectId) { window.open(projectExportUrl(m.projectId), "_blank"); flash(`Fiche ZIP · ${m.title}`, "ok"); }
      else flash(`Téléchargement STL · ${m.title}`, "ok");
    });
  }
  function remix(m: GModel) {
    gate(`Connecte-toi pour remixer « ${m.title} » dans le Studio.`, () => {
      if (m.real && m.projectId) nav(`/projects/${m.projectId}`);
      else nav(`/create?idea=${encodeURIComponent(m.title)}`);
    });
  }
  function like(m: GModel) {
    gate("Connecte-toi pour aimer et sauvegarder des modèles.", () => {
      // Derive the delta from one source of truth so the count can't desync from `liked`.
      const willLike = !liked[m.id];
      setLiked((s) => ({ ...s, [m.id]: willLike }));
      setLikes((s) => ({ ...s, [m.id]: (s[m.id] || 0) + (willLike ? 1 : -1) }));
    });
  }
  function generate() {
    if (!prompt.trim()) { flash("Décris ton objet d'abord", "bad"); return; }
    gate("Crée un compte pour générer ton objet et le télécharger.", () => nav(`/create?idea=${encodeURIComponent(prompt.trim())}`));
  }
  function openUpload() { gate("Crée un compte pour téléverser et vendre tes fichiers.", () => setUploadOpen(true)); }
  function toggleTarget(id: string) { setUp((s) => ({ ...s, targets: s.targets.includes(id) ? s.targets.filter((x) => x !== id) : [...s.targets, id] })); }
  function publishUpload() {
    if (!up.file) { flash("Choisis un fichier STL / 3MF / ZIP", "bad"); return; }
    if (!up.title.trim()) { flash("Donne un titre à ton modèle", "bad"); return; }
    const sale = up.monet === "sale";
    flash(sale ? `« ${up.title} » mis en vente à ${eur(up.price)}` : `« ${up.title} » publié gratuitement`, "ok");
    setUploadOpen(false);
    setUp({ file: "", title: "", desc: "", tags: "", cat: "vedette", use: "maison", monet: "free", price: 4.9, license: "CC-BY", targets: ["store_soclose"] });
  }

  const matches = (m: GModel) =>
    (cat === "all" || m.cat === cat) && (type === "all" || m.type === type) &&
    (use === "all" || m.use === use) && (price === "all" || (price === "free" ? m.price === 0 : m.price > 0));
  // Showcase catalog cards: only those with a real AI-rendered image (by index) are shown,
  // so the gallery never falls back to meaningless Iso cubes. As more images are generated
  // (manifest grows), the rest appear automatically.
  const mockModels = useMemo(
    () => MODELS.map((m, i) => ({ ...m, img: modelImgs[String(i)] })).filter((m) => m.img),
    [modelImgs],
  );
  // The Tendances / Récents views drive the sort so the same gallery reads differently.
  const effSort = view === "tendances" ? "pop" : view === "recents" ? "recent" : sort;
  // User/community models (real projects + showcase), filtered + sorted.
  const base = useMemo(() => {
    let r = [...realModels, ...mockModels].filter(matches);
    if (effSort === "recent") r = [...r].reverse();
    else if (effSort === "pop") r = [...r].sort((a, b) => b.downloads - a.downloads);
    else r = [...r].sort((a, b) => b.score * 1000 + b.likes - (a.score * 1000 + a.likes));
    return r;
  }, [realModels, mockModels, cat, type, use, price, effSort]);
  const aiList = useMemo(() => aiCards.filter(matches), [aiCards, cat, type, use, price]);
  // Weave the AI generations between the user cards (one every ~4), like Tripo's featured mix.
  const list = useMemo(() => {
    const out: GModel[] = []; let ai = 0;
    base.forEach((m, i) => { out.push(m); if ((i + 1) % 4 === 0 && ai < aiList.length) out.push(aiList[ai++]); });
    while (ai < aiList.length) out.push(aiList[ai++]);
    return out;
  }, [base, aiList]);
  // Per-use-case counts for the Categories view tiles.
  const useCounts = useMemo(() => {
    const all = [...realModels, ...mockModels, ...aiCards];
    const c: Record<string, number> = {};
    USES.slice(1).forEach(([slug]) => { c[slug] = all.filter((m) => m.use === slug).length; });
    return c;
  }, [realModels, mockModels, aiCards]);

  const examples = mode === "text"
    ? ["Support de téléphone incliné 60°", "Boîtier Raspberry Pi ventilé", "Engrenage module 2, 30 dents"]
    : ["Glisser une photo de pièce", "Logo en relief depuis une image", "Figurine depuis un croquis"];

  return (
    <div className="ex-body">
      <header className="ex-top">
        <div className="ex-brand"><div className="brand-mark">◈</div><div className="nm">3DPrintForge</div></div>
        <nav className="ex-nav">
          {([["explore", "Explorer"], ["tendances", "Tendances"], ["recents", "Récents"], ["categories", "Catégories"], ["tarifs", "Tarifs"]] as [View, string][]).map(([v, label]) => (
            <a key={v} className={view === v ? "on" : ""} onClick={() => { setView(v); window.scrollTo({ top: 0 }); }}>{tr(label)}</a>
          ))}
        </nav>
        <div className="spacer" />
        <div className="langsw">
          <button className="btn ghost sm" onClick={() => setLangOpen((o) => !o)} title={tr("Langue")}>{lang.toUpperCase()} <Icon name="arrowRight" size={11} /></button>
          {langOpen && (
            <div className="langmenu" onMouseLeave={() => setLangOpen(false)}>
              {LANGS.map((l) => <button key={l.id} className={l.id === lang ? "on" : ""} onClick={() => { setLang(l.id); setLangOpen(false); }}>{l.flag} · {l.label}</button>)}
            </div>
          )}
        </div>
        {auth ? (
          <div className="row ex-actions" style={{ gap: 10 }}>
            <button className="btn sm" onClick={openUpload}><Icon name="upload" size={14} /> {tr("Téléverser")}</button>
            <button className="btn primary sm" onClick={() => nav("/dashboard")}><Icon name="zap" size={14} /> {tr("Ouvrir le Studio")}</button>
            <div className="userchip"><span className="av" style={{ background: "var(--accent-3)" }}>{(user[0] || "T").toUpperCase()}</span><span style={{ fontSize: 12.5, fontWeight: 500 }}>{user}</span></div>
          </div>
        ) : (
          <div className="row ex-actions" style={{ gap: 8 }}>
            <button className="btn sm" onClick={openUpload}><Icon name="upload" size={14} /> {tr("Téléverser & vendre")}</button>
            <button className="btn ghost sm" onClick={() => { setAuthTab("login"); setReason(""); setAuthOpen(true); }}>{tr("Se connecter")}</button>
            <button className="btn primary sm" onClick={() => { setAuthTab("register"); setReason(""); setAuthOpen(true); }}>{tr("Créer un compte")}</button>
          </div>
        )}
        <button className="ex-burger" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
          <Icon name={menuOpen ? "x" : "menu"} size={20} />
        </button>
      </header>

      {/* Mobile dropdown menu (burger) */}
      {menuOpen && (
        <>
          <div className="ex-mobile-backdrop" onClick={() => setMenuOpen(false)} />
          <nav className="ex-mobile">
            {([["explore", "Explorer"], ["tendances", "Tendances"], ["recents", "Récents"], ["categories", "Catégories"], ["tarifs", "Tarifs"]] as [View, string][]).map(([v, label]) => (
              <a key={v} className={view === v ? "on" : ""} onClick={() => { setView(v); setMenuOpen(false); window.scrollTo({ top: 0 }); }}>{tr(label)}</a>
            ))}
            <div className="ex-mobile-actions">
              {auth ? (
                <>
                  <button className="btn" onClick={() => { setMenuOpen(false); openUpload(); }}><Icon name="upload" size={14} /> {tr("Téléverser")}</button>
                  <button className="btn primary" onClick={() => { setMenuOpen(false); nav("/dashboard"); }}><Icon name="zap" size={14} /> {tr("Ouvrir le Studio")}</button>
                </>
              ) : (
                <>
                  <button className="btn" onClick={() => { setMenuOpen(false); openUpload(); }}><Icon name="upload" size={14} /> {tr("Téléverser & vendre")}</button>
                  <button className="btn ghost" onClick={() => { setMenuOpen(false); setAuthTab("login"); setReason(""); setAuthOpen(true); }}>{tr("Se connecter")}</button>
                  <button className="btn primary" onClick={() => { setMenuOpen(false); setAuthTab("register"); setReason(""); setAuthOpen(true); }}>{tr("Créer un compte")}</button>
                </>
              )}
            </div>
          </nav>
        </>
      )}

      <div className="ex-wrap">
        {view === "explore" && (<>
        {/* Hero create */}
        <section className="hero">
          <div className="glow" /><div className="grid-bg" />
          <div className="hero-inner">
            <div className="eyebrow" style={{ marginBottom: 10 }}>{tr("Atelier public · idée → imprimable")}</div>
            <h1>{tr("Crée des objets 3D imprimables, en une phrase.")}</h1>
            <div className="sub">{tr("Parcours librement la galerie. Pour télécharger, remixer ou générer, crée ton compte. Téléverse et")} <b style={{ color: "var(--text-2)" }}>{tr("vends tes fichiers")}</b> {tr("— gratuit ou au prix que tu fixes.")}</div>
            <div className="create-bar">
              <div className="seg-toggle">
                <button className={mode === "text" ? "on" : ""} onClick={() => setMode("text")}><Icon name="type" size={14} /> {tr("Texte → 3D")}</button>
                <button className={mode === "image" ? "on" : ""} onClick={() => setMode("image")}><Icon name="image" size={14} /> {tr("Image → 3D")}</button>
              </div>
              <div className="create-input">
                <Icon name="sparkle" size={16} />
                <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={mode === "text" ? tr("Décris l'objet à imprimer…") : tr("Décris l'image source / glisse une photo…")} onKeyDown={(e) => e.key === "Enter" && generate()} />
              </div>
              <button className="btn primary" onClick={generate} style={{ padding: "0 20px" }}><Icon name="sparkle" size={15} /> {tr("Générer")}</button>
            </div>
            <div className="hero-examples">
              <span className="eyebrow">{tr("exemples")}</span>
              {examples.map((ex) => <button key={ex} className="fchip" onClick={() => setPrompt(ex)}>{ex}</button>)}
            </div>
          </div>
        </section>

        {/* Featured AI generations — slider (kie.ai / nano-banana-pro) */}
        {heroSlides.length > 0 && (
          <section className="hslider">
            {heroSlides.map((s, i) => (
              <div key={s.id} className={`hslide ${i === slide ? "on" : ""}`} aria-hidden={i !== slide}>
                <img src={s.img} alt={s.title} loading={i === 0 ? "eager" : "lazy"} decoding="async" fetchPriority={i === 0 ? "high" : "auto"} />
                <div className="hslide-cap">
                  <div className="eyebrow"><span className="dot" /> {tr("À la une · généré par IA")}</div>
                  <h2 className="hslide-title">{s.title}</h2>
                  <div className="hslide-sub">{s.sub}</div>
                  <button className="btn primary" onClick={() => nav("/create")}><Icon name="sparkle" size={15} /> {tr("Créer le mien")}</button>
                </div>
              </div>
            ))}
            <button className="hslide-arr left" aria-label={tr("Précédent")} onClick={() => setSlide((slide - 1 + heroSlides.length) % heroSlides.length)}><Icon name="arrowRight" size={18} /></button>
            <button className="hslide-arr right" aria-label={tr("Suivant")} onClick={() => setSlide((slide + 1) % heroSlides.length)}><Icon name="arrowRight" size={18} /></button>
            <div className="hslide-dots">
              {heroSlides.map((s, i) => <button key={s.id} className={i === slide ? "on" : ""} aria-label={`Slide ${i + 1}`} onClick={() => setSlide(i)} />)}
            </div>
          </section>
        )}

        {/* Compatibility marquee — printers, hosts, slicers, CAD, marketplaces */}
        <BrandStrip tr={tr} />

        {/* Social proof band */}
        <div className="proof">
          {[["12 400+", "modèles publiés"], ["3 480", "créateurs actifs"], ["1,2 M", "impressions validées"], ["98 %", "passées au gate INSPECTOR"]].map(([v, l]) => (
            <div key={l} className="proof-item"><div className="pv">{v}</div><div className="pl">{tr(l)}</div></div>
          ))}
        </div>

        {/* How it works */}
        <div className="how">
          {([["sparkle", "Décris ou téléverse", "Texte, image ou ton propre STL — l'IA conçoit du CAD validé.", "var(--accent)"], ["shield", "L'INSPECTOR valide", "Manifold, parois, volume : seul l'imprimable passe.", "var(--ok)"], ["zap", "Imprime ou vends", "Télécharge, route vers ta ferme, ou mets en vente.", "var(--warn)"]] as const).map(([ic, t, d, c], i) => (
            <div key={t} className="how-step">
              <div className="how-ic" style={{ color: c, background: `color-mix(in oklab, ${c} 14%, transparent)`, borderColor: `color-mix(in oklab, ${c} 38%, var(--line-2))` }}><Icon name={ic} size={20} /></div>
              <div><div className="how-t">{tr(t)}</div><div className="how-d">{tr(d)}</div></div>
              <span className="how-n">0{i + 1}</span>
            </div>
          ))}
        </div>
        </>)}

        {/* Tendances / Récents banner (distinct framing per view) */}
        {(view === "tendances" || view === "recents") && (
          <section className="vbanner" style={heroSlides[view === "tendances" ? 2 : 1] ? { backgroundImage: `linear-gradient(90deg, rgba(4,7,12,0.92), rgba(4,7,12,0.35) 70%), url(${heroSlides[view === "tendances" ? 2 : 1].img})` } : undefined}>
            <div className="vbanner-cap">
              <div className="eyebrow"><span className="dot" /> {tr(view === "tendances" ? "Tendances" : "Récents")}</div>
              <h2 className="hslide-title">{tr(view === "tendances" ? "Les modèles qui montent" : "Les dernières créations")}</h2>
              <div className="hslide-sub">{tr(view === "tendances" ? "Les plus téléchargés et likés cette semaine." : "Fraîchement généré et validé par l'INSPECTOR.")}</div>
            </div>
          </section>
        )}

        {/* Categories view */}
        {view === "categories" && (
          <CategoriesView tr={tr} counts={useCounts} imgs={catImgs} pick={(slug) => { setUse(slug); setCat("all"); setView("explore"); window.scrollTo({ top: 0 }); }} />
        )}

        {/* Pricing view */}
        {view === "tarifs" && (
          <PricingView tr={tr} banner={pricingBanner} onStart={() => nav("/create")} onDash={() => nav("/dashboard")} onUpload={openUpload} />
        )}

        {view !== "tarifs" && view !== "categories" && (<>
        {/* Filters — one fine, discreet line of selectors */}
        <div className="filterbar">
          <div className="fsel"><label>{tr("Catégorie")}</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)}>
              <option value="all">{tr("Tous")}</option><option value="vedette">{tr("En vedette")}</option><option value="tendance">{tr("Tendances")}</option><option value="recent">{tr("Récents")}</option>
            </select>
          </div>
          <div className="fsel"><label>{tr("Type")}</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="all">{tr("Tous")}</option><option value="parametric">{tr("CAD paramétrique")}</option><option value="generative">{tr("Génératif")}</option>
            </select>
          </div>
          <div className="fsel"><label>{tr("Cas d'usage")}</label>
            <select value={use} onChange={(e) => setUse(e.target.value)}>
              {USES.map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}
            </select>
          </div>
          <div className="fsel"><label>{tr("Prix")}</label>
            <select value={price} onChange={(e) => setPrice(e.target.value)}>
              <option value="all">{tr("Tous")}</option><option value="free">{tr("Gratuit")}</option><option value="paid">{tr("À vendre")}</option>
            </select>
          </div>
          <span className="fcount">{list.length} {tr("modèle(s)")}</span>
          <div className="fsel"><label>{tr("Trier")}</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)} disabled={view !== "explore"}>
              <option value="reco">{tr("Recommandés")}</option><option value="recent">{tr("Récents")}</option><option value="pop">{tr("Populaires")}</option>
            </select>
          </div>
        </div>

        {/* Gallery */}
        <div className="gallery">
          {list.map((m) => {
            const lc = (likes[m.id] || 0) + m.likes;
            return (
              <div key={m.id} className="gcard">
                <div className="gthumb" style={{ height: m.h }}>
                  {m.ai
                    ? <span className="badge ai-badge"><Icon name="sparkle" size={11} /> IA</span>
                    : <span className="badge score" style={{ background: "#0a0e16cc", color: scoreColor(m.score), borderColor: "transparent" }}><Icon name="shield" size={11} />{Math.round(m.score * 100)}</span>}
                  <span className="typ badge" style={{ background: "#0a0e16cc", borderColor: "transparent", color: m.type === "parametric" ? "var(--accent-3)" : "#c8a8ff" }}>{m.ai ? "nano-banana" : m.type === "parametric" ? "CAD" : "génératif"}</span>
                  {m.img && !imgFail[m.id] ? (
                    <img src={m.img} alt={m.title} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: m.ai || !m.real ? "cover" : "contain", padding: m.ai || !m.real ? 0 : 14 }} onError={() => setImgFail((s) => ({ ...s, [m.id]: true }))} />
                  ) : (
                    <Iso color={matColor(m.material)} size={Math.min(m.h - 30, 210)} />
                  )}
                  <span className={`gprice ${m.price ? "paid" : "free"}`}>{m.price ? eur(m.price) : tr("Gratuit")}</span>
                  <div className="govl">
                    {m.ai ? (
                      <button className="btn sm primary" onClick={() => remix(m)}><Icon name="sparkle" size={14} /> {tr("Générer similaire")}</button>
                    ) : (
                      <>
                        <button className="btn sm primary" onClick={() => download(m)}>{auth ? <Icon name={m.price ? "zap" : "download"} size={14} /> : <Icon name="lock" size={13} />} {m.price ? tr("Acheter") : tr("STL")}</button>
                        <button className="btn sm" onClick={() => remix(m)}><Icon name="remix" size={13} /> {tr("Remixer")}</button>
                      </>
                    )}
                    <button className="btn sm icon" title="J'aime" onClick={() => like(m)} style={liked[m.id] ? { color: "var(--bad)", borderColor: "var(--bad-line)" } : undefined}><Icon name="heart" size={14} /></button>
                  </div>
                </div>
                <div className="gmeta">
                  <div className="ttl">{m.title}</div>
                  <div className="by">
                    <span className="av" style={{ background: m.ac }}>{(m.author[0] || "?").toUpperCase()}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{m.author}</span>
                    <span className="badge" style={{ marginLeft: "auto", fontSize: 10 }}>{m.material}</span>
                  </div>
                  <div className="gstats">
                    <span className="s"><Icon name="download" size={12} />{(m.downloads / 1000).toFixed(1)}k</span>
                    <span className="s" style={liked[m.id] ? { color: "var(--bad)" } : undefined}><Icon name="heart" size={12} />{lc}</span>
                    <span className="s gate" style={{ marginLeft: "auto", color: m.price ? "var(--accent-3)" : "var(--muted)" }}>{m.price ? eur(m.price) : tr("Gratuit")}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>)}

        {/* Creator monetisation CTA band */}
        {view === "explore" && (
        <section className="sell-band">
          <div className="sell-glow" />
          <div className="sell-inner">
            <div>
              <div className="eyebrow" style={{ marginBottom: 10 }}>{tr("Pour les créateurs")}</div>
              <h2 style={{ fontSize: 28, fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}>{tr("Transforme tes modèles en revenus.")}</h2>
              <p className="muted" style={{ fontSize: 14, marginTop: 10, maxWidth: 520, lineHeight: 1.55 }}>{tr("Publie gratuitement pour la portée, ou fixe ton prix et vends sur la boutique SoClose et les marketplaces. Tu gardes")} <b style={{ color: "var(--accent-3)" }}>90 %</b> {tr("sur les ventes directes.")}</p>
              <div className="row" style={{ gap: 10, marginTop: 18 }}>
                <button className="btn primary" onClick={openUpload}><Icon name="upload" size={15} /> {tr("Téléverser un modèle")}</button>
                <button className="btn" onClick={() => { setAuthTab("register"); setAuthOpen(true); }}>{tr("Devenir créateur")}</button>
              </div>
            </div>
            <div className="sell-stats">
              {[["90 %", "reversés au créateur"], ["0 €", "pour publier"], ["12+", "marketplaces"], ["48 h", "délai de paiement"]].map(([v, l]) => (
                <div key={l} className="sell-stat"><div className="sv">{v}</div><div className="sl">{tr(l)}</div></div>
              ))}
            </div>
          </div>
        </section>
        )}
      </div>

      {/* Community CTA (pre-footer) */}
      <section className="home-community">
        <div className="home-community-head">
          <div className="eyebrow"><span className="dot" /> {tr("Communauté")}</div>
          <h2>{tr("Rejoins la communauté des makers")}</h2>
        </div>
        <CommunityCard banner={communityBanner} />
      </section>

      {/* Footer */}
      <footer className="ex-foot">
        <div className="foot-inner">
          <div className="foot-brand">
            <div className="ex-brand"><div className="brand-mark">◈</div><div className="nm">3DPrintForge</div></div>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 12, maxWidth: 260, lineHeight: 1.5 }}>{tr("L'atelier 3D de bout en bout : idée → maillage validé → imprimé ou vendu.")}</p>
            <div className="foot-legal">
              <b>{SOCLOSE.name}</b>
              <span>{SOCLOSE.uenLabel} · {SOCLOSE.jurisdiction} {SG_FLAG}</span>
              <span>{SOCLOSE.address.line1}, {SOCLOSE.address.line2}</span>
              <span>{SOCLOSE.address.country} {SOCLOSE.address.postal}</span>
              <a href={`mailto:${SOCLOSE.contact.email}`} style={{ color: "var(--muted)" }}>{SOCLOSE.contact.email}</a>
            </div>
          </div>
          {([
            ["Produit", [
              ["Explorer", () => { setView("explore"); window.scrollTo({ top: 0 }); }],
              ["Générer", () => nav("/create")],
              ["Studio 3D", () => nav("/studio")],
              ["Tarifs", () => { setView("tarifs"); window.scrollTo({ top: 0 }); }],
            ]],
            ["Créateurs", [
              ["Téléverser", openUpload],
              ["Vendre", openUpload],
              ["Marketplaces", () => { setView("tarifs"); window.scrollTo({ top: 0 }); }],
              ["Revenus", () => { setView("tarifs"); window.scrollTo({ top: 0 }); }],
            ]],
            ["Ressources", [
              ["Documentation", () => window.open("/api/v1/docs", "_blank", "noopener")],
              ["API", () => window.open("/api/v1/docs", "_blank", "noopener")],
              ["INSPECTOR", () => { setView("explore"); setTimeout(() => document.querySelector(".how")?.scrollIntoView({ behavior: "smooth" }), 80); }],
              ["Statut", () => window.open("/api/v1/health", "_blank", "noopener")],
            ]],
          ] as [string, [string, () => void][]][]).map(([h, links]) => (
            <div key={h} className="foot-col">
              <div className="foot-h">{tr(h)}</div>
              {links.map(([l, act]) => <a key={l} onClick={act}>{tr(l)}</a>)}
            </div>
          ))}
          <div className="foot-col">
            <div className="foot-h">{tr("Légal")}</div>
            <Link to="/privacy">{tr("Confidentialité")}</Link>
            <Link to="/terms">{tr("Conditions")}</Link>
            <Link to="/cookies">{tr("Cookies")}</Link>
            <Link to="/refund">{tr("Remboursement")}</Link>
            <Link to="/legal">{tr("Mentions légales")}</Link>
          </div>
        </div>
        <div className="foot-bar">
          <span className="muted" style={{ fontSize: 12 }}>© {new Date().getFullYear()} {SOCLOSE.name} · SoClose Forge suite</span>
          <span className="foot-legal-links">
            <span className="eyebrow">{tr("Fait à Singapour")} {SG_FLAG}</span>
            <span className="muted" style={{ fontSize: 12 }}>· {tr("Prix en")} {SOCLOSE.currency.code} ({SOCLOSE.currency.symbol})</span>
          </span>
        </div>
      </footer>

      {/* Upload & sell modal */}
      {uploadOpen && (
        <div className="modal-backdrop" onClick={() => setUploadOpen(false)}>
          <div className="modal up-modal" onClick={(e) => e.stopPropagation()}>
            <div className="row between" style={{ marginBottom: 16 }}>
              <div><div className="eyebrow" style={{ marginBottom: 6 }}>{tr("Créateur")}</div><h2 style={{ fontSize: 18 }}>{tr("Téléverser un modèle")}</h2></div>
              <button className="btn ghost icon sm" onClick={() => setUploadOpen(false)}><Icon name="x" size={16} /></button>
            </div>

            <div className={`dropzone ${up.file ? "filled" : ""}`} onClick={() => setUp({ ...up, file: "mon_modele.stl · 2.4 Mo" })}>
              {up.file ? <><Icon name="box" size={20} /><span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{up.file}</span><span className="badge ok"><Icon name="check" size={11} />{tr("prêt")}</span></>
                : <><Icon name="upload" size={22} /><div><div style={{ fontWeight: 600, fontSize: 13.5 }}>{tr("Glisse ton fichier ou clique pour choisir")}</div><div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{tr("STL · 3MF · OBJ · ZIP — jusqu'à 500 Mo")}</div></div></>}
            </div>

            <div className="field" style={{ marginTop: 14 }}><label>{tr("Titre")}</label><input value={up.title} onChange={(e) => setUp({ ...up, title: e.target.value })} placeholder={tr("Support de caméra inclinable 60°")} /></div>
            <div className="field"><label>{tr("Description")}</label><textarea value={up.desc} onChange={(e) => setUp({ ...up, desc: e.target.value })} placeholder={tr("Usage, réglages conseillés, matière…")} style={{ minHeight: 70 }} /></div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>{tr("Cas d'usage")}</label><select value={up.use} onChange={(e) => setUp({ ...up, use: e.target.value })}>{USES.slice(1).map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}</select></div>
              <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>{tr("Tags")}</label><input value={up.tags} onChange={(e) => setUp({ ...up, tags: e.target.value })} placeholder="caméra, rail 2020, M3" /></div>
            </div>

            <div className="eyebrow" style={{ margin: "18px 0 9px" }}>{tr("Monétisation")}</div>
            <div className="monet">
              <button className={`monet-opt ${up.monet === "free" ? "on" : ""}`} onClick={() => setUp({ ...up, monet: "free" })}>
                <div className="row between"><span style={{ fontWeight: 600, fontSize: 14, fontFamily: "var(--font-display)" }}>{tr("Gratuit")}</span><Icon name={up.monet === "free" ? "check" : "download"} size={16} /></div>
                <div className="muted" style={{ fontSize: 12, marginTop: 5, lineHeight: 1.4 }}>{tr("Partage libre pour un max de portée et de téléchargements.")}</div>
              </button>
              <button className={`monet-opt ${up.monet === "sale" ? "on" : ""}`} onClick={() => setUp({ ...up, monet: "sale" })}>
                <div className="row between"><span style={{ fontWeight: 600, fontSize: 14, fontFamily: "var(--font-display)" }}>{tr("En vente")}</span><Icon name={up.monet === "sale" ? "check" : "zap"} size={16} /></div>
                <div className="muted" style={{ fontSize: 12, marginTop: 5, lineHeight: 1.4 }}>{tr("Tu fixes le prix. Encaisse via la boutique SoClose ou un canal payant.")}</div>
              </button>
            </div>
            {up.monet === "sale" && (
              <div className="row" style={{ gap: 10, marginTop: 12 }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>{tr("Prix (€)")}</label><input type="number" step="0.5" min="0" value={up.price} onChange={(e) => setUp({ ...up, price: Number(e.target.value) })} /></div>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}><label>{tr("Licence")}</label><select value={up.license} onChange={(e) => setUp({ ...up, license: e.target.value })}><option>{tr("Commerciale (vente)")}</option><option>CC-BY</option><option>CC-BY-NC</option></select></div>
                <div style={{ flex: 1.2, alignSelf: "flex-end", paddingBottom: 10 }}><span className="muted" style={{ fontSize: 11.5 }}>{tr("Tu touches")} <b style={{ color: "var(--accent-3)" }}>~{Math.round(up.price * 0.9 * 100) / 100} €</b> {tr("/ vente sur la boutique SoClose.")}</span></div>
              </div>
            )}

            <div className="eyebrow" style={{ margin: "18px 0 9px" }}>{tr("Destinations")}</div>
            <div className="targets">
              {PUB_TARGETS.filter((t) => (up.monet === "free" ? true : t.sell)).map((t) => {
                const on = up.targets.includes(t.id);
                return (
                  <button key={t.id} className={`tgt ${on ? "on" : ""}`} onClick={() => toggleTarget(t.id)}>
                    <div className="row between">
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</span>
                      <span className={`badge ${t.mode === "auto" ? "ok" : ""}`} style={{ fontSize: 9.5 }}>{t.mode === "auto" ? "upload API" : tr("manuel")}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>{t.note}</div>
                    {on && <span className="tgt-check"><Icon name="check" size={12} /></span>}
                  </button>
                );
              })}
            </div>
            <div className="muted row" style={{ gap: 6, fontSize: 11, marginTop: 10 }}><Icon name="shield" size={12} /> {tr("Validé par l'INSPECTOR avant mise en ligne. Upload réel : credentials du site + confirmation.")}</div>

            <div className="row between" style={{ marginTop: 18 }}>
              <button className="btn ghost" onClick={() => setUploadOpen(false)}>{tr("Annuler")}</button>
              <button className="btn primary" onClick={publishUpload}><Icon name={up.monet === "sale" ? "zap" : "upload"} size={15} /> {up.monet === "sale" ? tr("Mettre en vente") : tr("Publier gratuitement")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      {authOpen && (
        <div className="modal-backdrop" onClick={() => setAuthOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="row between" style={{ marginBottom: 16 }}>
              <div className="ex-brand"><div className="brand-mark" style={{ width: 30, height: 30, fontSize: 15 }}>◈</div><div className="nm" style={{ fontSize: 15 }}>3DPrintForge</div></div>
              <button className="btn ghost icon sm" onClick={() => setAuthOpen(false)}><Icon name="x" size={16} /></button>
            </div>
            {reason && <div className="gate-banner"><div className="ic"><Icon name="lock" size={16} /></div><div style={{ fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.45 }}>{reason}</div></div>}
            <div className="auth-tabs">
              <button className={authTab === "login" ? "on" : ""} onClick={() => setAuthTab("login")}>{tr("Connexion")}</button>
              <button className={authTab === "register" ? "on" : ""} onClick={() => setAuthTab("register")}>{tr("Inscription")}</button>
            </div>
            <button className="btn social-btn" onClick={doAuth}><span style={{ fontWeight: 700, color: "#4285f4" }}>G</span> {tr("Continuer avec Google")}</button>
            <button className="btn social-btn" onClick={doAuth}><Icon name="user" size={15} /> {tr("Continuer avec GitHub")}</button>
            <div className="auth-sep">{tr("ou par e-mail")}</div>
            {authTab === "register" && <div className="field"><label>{tr("Nom")}</label><input id="au-name" placeholder={tr("Ton nom")} /></div>}
            <div className="field"><label>E-mail</label><input type="email" placeholder="toi@exemple.com" /></div>
            <div className="field"><label>{tr("Mot de passe")}</label><input type="password" placeholder="••••••••" /></div>
            <button className="btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 6 }} onClick={doAuth}>
              {authTab === "register" ? tr("Créer mon compte") : tr("Se connecter")}
            </button>
            <div className="muted" style={{ fontSize: 11, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
              {authTab === "register" ? tr("En créant un compte, tu acceptes les conditions et la politique de confidentialité.") : tr("Pas encore de compte ?") + " "}
              {authTab === "login" && <a style={{ color: "var(--accent-3)", cursor: "pointer" }} onClick={() => setAuthTab("register")}>{tr("Inscris-toi")}</a>}
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.k || "ok"}`}><Icon name={toast.k === "bad" ? "x" : "check"} size={15} />{toast.m}</div>}
    </div>
  );
}

const CAT_TILES: [string, string, string][] = [
  ["maison", "Maison", "dashboard"], ["atelier", "Atelier", "generators"], ["deco", "Déco", "sparkle"],
  ["rc", "RC & Drone", "route"], ["meca", "Mécanique", "cpu"], ["optique", "Optique", "studio"],
];
function CategoriesView({ tr, counts, imgs, pick }: { tr: (s: string) => string; counts: Record<string, number>; imgs: Record<string, string>; pick: (slug: string) => void }) {
  return (
    <div>
      <section className="vbanner cats-banner">
        <div className="vbanner-cap">
          <div className="eyebrow"><span className="dot" /> {tr("Catégories")}</div>
          <h2 className="hslide-title">{tr("Parcourir par catégorie")}</h2>
          <div className="hslide-sub">{tr("Trouve la pièce qu'il te faut, par usage.")}</div>
        </div>
      </section>
      <div className="cat-grid">
        {CAT_TILES.map(([slug, label, ico]) => (
          <button key={slug} className={`cat-tile ${imgs[slug] ? "has-bg" : ""}`} onClick={() => pick(slug)}
            style={imgs[slug] ? { backgroundImage: `linear-gradient(180deg, rgba(8,12,19,0.55) 0%, rgba(8,12,19,0.9) 78%), url(${imgs[slug]})` } : undefined}>
            <div className="cat-ic"><Icon name={ico} size={22} /></div>
            <div className="cat-name">{tr(label)}</div>
            <div className="cat-count">{counts[slug] ?? 0} {tr("pièces")}</div>
            <span className="cat-go">{tr("Voir la catégorie")} <Icon name="arrowRight" size={13} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PricingView({ tr, banner, onStart, onDash, onUpload }: { tr: (s: string) => string; banner?: string; onStart: () => void; onDash: () => void; onUpload: () => void }) {
  const plans = [
    { name: "Maker", price: tr("Gratuit pour toujours"), tag: "", hot: false, cta: tr("Ouvrir le Studio"), act: onStart,
      feats: ["CAD paramétrique + 19 générateurs", "Gate INSPECTOR illimité", "Export STL / 3MF / G-code", "Génération IA (Ollama local)", "Parcours libre de la galerie"] },
    { name: "Studio", price: "19 € · " + tr("Par mois"), tag: tr("Le plus populaire"), hot: true, cta: tr("Devenir créateur"), act: onUpload,
      feats: ["Tout Maker, plus :", "Génération IA cloud (TRELLIS / Hunyuan3D)", "Slicing PrusaSlicer + devis", "Vente sur 12 marketplaces", "90 % reversés sur tes ventes", "Routage ferme d'impression"] },
    { name: "Atelier", price: tr("Sur devis"), tag: "", hot: false, cta: tr("Ouvrir le Studio"), act: onDash,
      feats: ["Tout Studio, plus :", "Multi-imprimantes + Spoolman", "Boutique store.soclose.co dédiée", "Connecteurs MMF / Thingiverse", "Support prioritaire", "SLA & facturation entreprise"] },
  ];
  return (
    <div className="pricing">
      <section className="vbanner pricing-banner" style={banner ? { backgroundImage: `linear-gradient(90deg, rgba(4,7,12,0.92), rgba(4,7,12,0.45) 75%), url(${banner})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
        <div className="vbanner-cap">
          <div className="eyebrow"><span className="dot" /> {tr("Tarifs")}</div>
          <h2 className="hslide-title">{tr("Choisis ta formule")}</h2>
          <div className="hslide-sub">{tr("Commence gratuitement, passe au niveau supérieur quand tu vends.")}</div>
        </div>
      </section>
      <div className="price-grid">
        {plans.map((p) => (
          <div key={p.name} className={`price-card ${p.hot ? "hot" : ""}`}>
            {p.tag && <span className="price-flag">{p.tag}</span>}
            <div className="price-name">{p.name}</div>
            <div className="price-amt">{p.price}</div>
            <ul className="price-feats">{p.feats.map((f) => <li key={f}><Icon name="check" size={13} /> {tr(f)}</li>)}</ul>
            <button className={`btn ${p.hot ? "primary" : "ghost"}`} style={{ width: "100%", justifyContent: "center" }} onClick={p.act}>{p.cta}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
